import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  RefObject,
} from "react";
// 1. Use TYPE-ONLY imports. These are erased at build time and won't crash SSR.
import type { PoseDetector } from "@tensorflow-models/pose-detection";
import posthog from "../utils/posthog";
import speechService from "../utils/service/speechService";
import { videoConstraints, voiceOverAssetsPath } from "../utils/constants";

/**
 * useFaceScan Hook with PostHog Analytics
 * Refactored for Zero-Config SSR/CSR Compatibility
 */

// Debounce utility
function debounce(fn: (...args: any) => void, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: any) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

// Global Singleton Cache (Preserves state across re-renders)
let preloadedDetector: PoseDetector | null = null;
let isPreloading = false;

// 2. Async Preloader with Dynamic Imports
export const preloadDetector = async (): Promise<PoseDetector | null> => {
  // Guard: Never run on server
  if (typeof window === "undefined") return null;
  if (preloadedDetector) return preloadedDetector;
  if (isPreloading) {
    // Simple wait mechanism if already loading
    while (isPreloading) {
      await new Promise(r => setTimeout(r, 100));
      if (preloadedDetector) return preloadedDetector;
    }
  }

  isPreloading = true;

  try {
    // DYNAMIC IMPORTS: Only fetch these heavy bundles in the browser
    const [tf, poseDetection] = await Promise.all([
      import("@tensorflow/tfjs"),
      import("@tensorflow-models/pose-detection")
    ]);

    // Initialize Backend
    await tf.setBackend("webgl");
    await tf.ready();

    const detector = await poseDetection.createDetector(
      poseDetection.SupportedModels.BlazePose,
      {
        runtime: "tfjs",
        modelType: "full",
      }
    );

    // Dummy video warmup (Performance Optimization)
    // This runs the model once on a blank canvas to "wake up" the GPU
    // so the first user frame detects instantly.
    const dummyCanvas = document.createElement("canvas");
    dummyCanvas.width = typeof videoConstraints.width === "number" ? videoConstraints.width : videoConstraints.width.ideal ?? videoConstraints.width.max ?? 1280;
    dummyCanvas.height = typeof videoConstraints.height === "number" ? videoConstraints.height : videoConstraints.height.ideal ?? videoConstraints.height.max ?? 720;
    const ctx = dummyCanvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, dummyCanvas.width, dummyCanvas.height);
      try {
        await detector.estimatePoses(dummyCanvas);
      } catch (e) {
        console.warn("Warmup frame failed (non-fatal):", e);
      }
    }

    preloadedDetector = detector;
    return detector;
  } catch (err) {
    console.error("Failed to preload detector:", err);
    return null;
  } finally {
    isPreloading = false;
  }
};

type Point = [number, number, number];

function useFaceScan({
  faceScanId,
  onValidPose,
  onScanComplete,
  onModelReady,
  onStartRecording,
  shopDomain,
  isError,
  isSuccess,
}: {
  faceScanId: string;
  onValidPose?: () => void;
  onScanComplete?: () => void;
  onModelReady?: () => void;
  onStartRecording: () => void;
  shopDomain: string;
  isError?: boolean;
  isSuccess?: boolean;
}) {
  const detectorRef = useRef<PoseDetector | null>(null);
  const [scanStage, setScanStage] = useState(0);
  const [consecutiveValid, setConsecutiveValid] = useState(0);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isScanningActive, setIsScanningActive] = useState(false);
  const isInferencingRef = useRef(false);
  // Persistent offscreen canvas for inference input.
  // tf.browser.fromPixels(videoElement) calls gl.texImage2D with the video,
  // which returns black frames on SwiftShader (headless CI) because video decoded
  // frames aren't in SW-composited memory. Drawing to a canvas first makes the
  // pixels CPU-accessible so SwiftShader can read them correctly.
  const inferenceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const scanStageRef = useRef(scanStage);
  const consecutiveValidRef = useRef(consecutiveValid);
  const validityBufferRef = useRef<boolean[]>([]);
  const lastSpokenMessageRef = useRef("");
  const lastSpokenTimeRef = useRef(0);
  const voiceEnabledRef = useRef(false);
  
  const VALIDITY_BUFFER_LENGTH = 10;
  const CONSECUTIVE_VALID_LENGTH = 2;
  const TOTAL_STAGES = 4;
  const POSE_TRACKING_INTERVAL = 4000;
  const lastPoseTrackingTimeRef = useRef(0);

  // Safe iOS detection (SSR safe)
  const isIOS = typeof navigator !== 'undefined' && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );

  const directionMessages = useMemo(
    () => [
      "Face front",
      "Turn head fully left",
      "Turn head fully right",
      "Smile facing front",
    ],
    []
  );

  const debouncedTrackPoseDetection = useMemo(
    () =>
      debounce((payload) => {
        posthog.capture(`${shopDomain}/face_scan_pose_detection`, payload);
      }, 4000),
    [shopDomain]
  );

  const trackPoseDetection = useCallback(
    (
      faceKeypoints: Point[],
      bodyKeypoints: Point[],
      stage: number,
      headValid: boolean,
      bodyInvalid: boolean,
      consecutiveValidCount: number
    ) => {
      const now = Date.now();
      if (now - lastPoseTrackingTimeRef.current < POSE_TRACKING_INTERVAL) {
        return;
      }

      lastPoseTrackingTimeRef.current = now;

      try {
        const nose = faceKeypoints[0] || [0, 0, 0];
        const leftEye = faceKeypoints[2] || [0, 0, 0];
        const rightEye = faceKeypoints[5] || [0, 0, 0];
        const leftMouth = faceKeypoints[9] || [0, 0, 0];
        const rightMouth = faceKeypoints[10] || [0, 0, 0];

        const faceKeypointScores = faceKeypoints
          .map((kp: Point) => kp[2])
          .filter((score: number) => score > 0);
        const avgFaceScore =
          faceKeypointScores.length > 0
            ? faceKeypointScores.reduce((a, b) => a + b, 0) /
              faceKeypointScores.length
            : 0;

        const bodyKeypointScores = bodyKeypoints
          .map((kp: Point) => kp[2])
          .filter((score: number) => score > 0);
        const avgBodyScore =
          bodyKeypointScores.length > 0
            ? bodyKeypointScores.reduce((a: number, b: number) => a + b, 0) /
              bodyKeypointScores.length
            : 0;

        const eyeDistance = Math.abs(leftEye[0] - rightEye[0]);
        const noseToRight = nose[0] - rightEye[0];
        const noseToLeft = leftEye[0] - nose[0];

        let expectedPosition = "frontal";
        if (stage === 1) expectedPosition = "left";
        else if (stage === 2) expectedPosition = "right";
        else if (stage === 3) expectedPosition = "frontal_smile";

        const payload = {
          faceScanId,
          stage,
          timestamp: new Date().toISOString(),
          data: JSON.stringify({
            headValid,
            bodyInvalid,
            consecutiveValid: consecutiveValidCount,
            avgFaceScore: parseFloat(avgFaceScore.toFixed(3)),
            avgBodyScore: parseFloat(avgBodyScore.toFixed(3)),
            faceKeypointsDetected: faceKeypointScores.length,
            bodyKeypointsDetected: bodyKeypointScores.length,
            eyeDistance: parseFloat(eyeDistance.toFixed(2)),
            noseToRightRatio: parseFloat((noseToRight / eyeDistance).toFixed(3)),
            noseToLeftRatio: parseFloat((noseToLeft / eyeDistance).toFixed(3)),
            noseScore: parseFloat(nose[2].toFixed(3)),
            leftEyeScore: parseFloat(leftEye[2].toFixed(3)),
            rightEyeScore: parseFloat(rightEye[2].toFixed(3)),
            leftMouthScore: parseFloat(leftMouth[2].toFixed(3)),
            rightMouthScore: parseFloat(rightMouth[2].toFixed(3)),
            expectedPosition,
          }),
        };
        debouncedTrackPoseDetection(payload);
      } catch (error) {
        console.warn("Failed to track pose detection:", error);
      }
    },
    [faceScanId, debouncedTrackPoseDetection]
  );

  const enableVoiceCommands = () => {
    voiceEnabledRef.current = true;
    if (typeof window !== 'undefined' && isIOS && "speechSynthesis" in window) {
      const silentUtterance = new SpeechSynthesisUtterance("");
      silentUtterance.volume = 0;
      speechSynthesis.speak(silentUtterance);
    }
  };

  const startScanSequence = () => {
    posthog.capture(`${shopDomain}/face_scan_detection_started`, {
      faceScanId,
      stage: scanStageRef.current,
      timestamp: new Date().toISOString(),
      stageName: directionMessages[scanStageRef.current],
    });

    setTimeout(() => {
      setIsScanningActive(true);
    }, 1500);
  };

  const isHeadInPosition = (stage: number, keypoints: Point[]) => {
    // ... logic unchanged ...
    const nose = 0;
    const leftEye = 2;
    const rightEye = 5;
    const leftMouth = 9;
    const rightMouth = 10;

    const minScore = 0.2;
    if (
      !keypoints[nose] || !keypoints[leftEye] || !keypoints[rightEye] ||
      keypoints[nose][2] < minScore ||
      keypoints[leftEye][2] < minScore ||
      keypoints[rightEye][2] < minScore
    ) {
      return false;
    }

    const eyesToMouthVerticalDistance =
      0.5 * (keypoints[rightMouth][1] + keypoints[leftMouth][1]) -
      0.5 * (keypoints[rightEye][1] + keypoints[leftEye][1]);

    const faceTiltedness =
      (0.5 * (keypoints[rightMouth][1] + keypoints[leftMouth][1]) -
        keypoints[nose][1]) /
      eyesToMouthVerticalDistance;

    const faceIsTilted = faceTiltedness > 0.7 || faceTiltedness < 0.3;

    const faceIsVertical =
      Math.abs(keypoints[leftEye][1] - keypoints[rightEye][1]) <
        0.5 * eyesToMouthVerticalDistance &&
      Math.abs(keypoints[leftMouth][1] - keypoints[rightMouth][1]) <
        0.5 * eyesToMouthVerticalDistance;

    const eyeDistance = keypoints[leftEye][0] - keypoints[rightEye][0];
    const noseToRight = keypoints[nose][0] - keypoints[rightEye][0];
    const noseToLeft = keypoints[leftEye][0] - keypoints[nose][0];

    const frontalFace =
      noseToRight > 0.4 * eyeDistance && noseToRight < 0.6 * eyeDistance;
    const fullLeft = noseToRight > 0.85 * eyeDistance;
    const fullRight = noseToLeft > 0.85 * eyeDistance;

    switch (stage) {
      case 0:
        return !faceIsTilted && faceIsVertical && frontalFace;
      case 1:
        return !faceIsTilted && faceIsVertical && fullLeft;
      case 2:
        return !faceIsTilted && faceIsVertical && fullRight;
      case 3:
        return !faceIsTilted && faceIsVertical && frontalFace;
      default:
        return false;
    }
  };

  const faceScanDetector = async (
    webcamRef: RefObject<HTMLVideoElement | null>,
    canvasRef: RefObject<HTMLCanvasElement | null>
  ) => {
    if (
      !detectorRef.current ||
      !webcamRef.current ||
      !isModelLoaded ||
      !isScanningActive
    )
      return;

    // Safety: ensure video is actually playing with data
    if (webcamRef.current.readyState < 2) return;

    // Prevent concurrent inference calls — important on slow GPUs (SwiftShader/CI)
    // where a single estimatePoses() can take longer than the 500ms interval.
    if (isInferencingRef.current) return;
    isInferencingRef.current = true;

    try {
      const vw = webcamRef.current.videoWidth || 720;
      const vh = webcamRef.current.videoHeight || 1280;
      if (!inferenceCanvasRef.current) {
        inferenceCanvasRef.current = document.createElement("canvas");
        inferenceCanvasRef.current.width = vw;
        inferenceCanvasRef.current.height = vh;
      }
      const ictx = inferenceCanvasRef.current.getContext("2d");
      if (ictx) ictx.drawImage(webcamRef.current, 0, 0, vw, vh);
      const poses = await detectorRef.current.estimatePoses(
        ictx ? inferenceCanvasRef.current : webcamRef.current
      );
      if (poses.length > 0) {
        const faceKeypoints: Point[] = [];
        const bodyKeypoints: Point[] = [];
        poses[0].keypoints.forEach((landmark, idx) => {
          const score = landmark.score ?? 0;
          const point: Point = [landmark.x ?? -1, landmark.y ?? -1, score];
          if (idx <= 10) faceKeypoints.push(point);
          else bodyKeypoints.push(point);
        });

        // Optional drawing
        if (canvasRef?.current) {
          const ctx = canvasRef.current.getContext("2d");
          const { width, height } = canvasRef.current;
          if (ctx) {
            ctx.clearRect(0, 0, width, height);

            const progress = Math.min(
              consecutiveValidRef.current / CONSECUTIVE_VALID_LENGTH,
              1
            );
            const radius = height * 0.35;
            ctx.beginPath();
            ctx.strokeStyle = "#00ff00";
            ctx.lineWidth = 6;
            ctx.arc(
              width / 2,
              height / 2,
              radius + 10,
              -Math.PI / 2,
              -Math.PI / 2 + progress * 2 * Math.PI
            );
            ctx.stroke();
          }
        }

        const headValid = isHeadInPosition(scanStageRef.current, faceKeypoints);
        const bodyInvalid =
          bodyKeypoints.slice(2).filter((p) => p[2] > 0.7).length <= 2;

        validityBufferRef.current.push(headValid && bodyInvalid);
        if (validityBufferRef.current.length > VALIDITY_BUFFER_LENGTH) {
          validityBufferRef.current.shift();
        }

        const validCount = validityBufferRef.current.filter(Boolean).length;
        if (validCount >= 2) {
          const nextValid = consecutiveValidRef.current + 1;
          setConsecutiveValid(nextValid);
        } else {
          setConsecutiveValid(Math.max(0, consecutiveValidRef.current - 1));
        }

        if (consecutiveValidRef.current >= CONSECUTIVE_VALID_LENGTH) {
          onValidPose?.();

          const nextStage = scanStageRef.current + 1;
          setConsecutiveValid(0);
          validityBufferRef.current = [];
          setScanStage(nextStage);
          setIsScanningActive(false);

          (async () => {
            await speechService.playAudio(
              `${voiceOverAssetsPath}face-scan-vos/Sound-effect.mp3`
            );
            posthog.capture(
              `${shopDomain}/face_scan_detection_stage_completed`,
              {
                faceScanId,
                completedStage: scanStageRef.current,
                nextStage,
                timestamp: new Date().toISOString(),
                totalStages: TOTAL_STAGES,
                stageName: directionMessages[scanStageRef.current],
              }
            );
            
            if (nextStage === 1 && onStartRecording) {
              onStartRecording();
            }
            if (nextStage >= TOTAL_STAGES) {
              onScanComplete?.();
            } else {
              let nextSound = null;
              switch (nextStage) {
                case 1: nextSound = "Left.mp3"; break;
                case 2: nextSound = "Right.mp3"; break;
                case 3: nextSound = "Smile.mp3"; break;
              }
              if (nextSound) {
                await speechService.playAudio(
                  `${voiceOverAssetsPath}face-scan-vos/${nextSound}`
                );
              }
              startScanSequence();
            }
          })();
        }

        trackPoseDetection(
          faceKeypoints,
          bodyKeypoints,
          scanStageRef.current,
          headValid,
          bodyInvalid,
          consecutiveValidRef.current
        );
      }
    } catch (err) {
      console.error("Pose detection error:", err);
    } finally {
      isInferencingRef.current = false;
    }
  };

  const initializeModels = async () => {
    // 3. Browser Guard
    if (typeof window === 'undefined') return;

    try {
      console.log("Initializing Face Scan...");
      // Calls our safe, dynamic preloader
      const detector = await preloadDetector();
      
      if (detector) {
        detectorRef.current = detector;
        console.log("Face scan model loaded successfully");
        setIsModelLoaded(true);
        onModelReady?.();
      }
    } catch (err) {
      console.error("Error initializing face scan:", err);
    }
  };

  const disposeModelAndTf = async () => {
    // Optional: We might NOT want to dispose if we want to keep the cache 
    // for re-mounting. But if you must dispose:
    try {
      setIsModelLoaded(false);
      // NOTE: We generally don't dispose the detector if we want to reuse it 
      // via the 'preloadedDetector' global variable.
      // If you dispose here, make sure to set 'preloadedDetector = null' too.
      /* if (detectorRef.current) {
        await detectorRef.current.dispose();
        detectorRef.current = null;
        preloadedDetector = null; // Clear global cache if disposing
      }
      */
    } catch (err) {
      console.error("Error disposing resources:", err);
    } finally {
      // Reset state
      setScanStage(0);
      setConsecutiveValid(0);
      scanStageRef.current = 0;
      consecutiveValidRef.current = 0;
      validityBufferRef.current = [];
    }
  };

  const resetScan = () => {
    posthog.capture(`${shopDomain}/face_scan_reset`, {
      faceScanId,
      stage: scanStageRef.current,
      timestamp: new Date().toISOString(),
      stageName: directionMessages[scanStageRef.current],
      consecutiveValid: consecutiveValidRef.current,
    });

    setScanStage(0);
    setConsecutiveValid(0);
    setIsScanningActive(false);
    scanStageRef.current = 0;
    consecutiveValidRef.current = 0;
    validityBufferRef.current = [];
    lastSpokenMessageRef.current = "";
    lastSpokenTimeRef.current = 0;
    voiceEnabledRef.current = false;
    if (utteranceRef.current) {
      speechSynthesis.cancel();
      utteranceRef.current = null;
    }
  };

  useEffect(() => {
    if (isError || isSuccess) return;
    initializeModels();
    return () => {
      disposeModelAndTf();
    };
  }, [isError, isSuccess]);

  useEffect(() => {
    scanStageRef.current = scanStage;
  }, [scanStage]);

  useEffect(() => {
    consecutiveValidRef.current = consecutiveValid;
  }, [consecutiveValid]);

  return {
    faceScanDetector,
    scanStage,
    setScanStage,
    consecutiveValid,
    isModelLoaded,
    resetScan,
    enableVoiceCommands,
    startScanSequence,
    isScanningActive,
  };
}

export default useFaceScan;